import { useCallback, useEffect, useState } from 'react';

import { Button, Modal, Form, Alert, Col, Row, Container } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Formik, Form as FormFormik, useFormikContext } from 'formik';
import TimeField from 'react-simple-timefield';
import { encode } from 'html-entities';
import { DateTime } from 'luxon';
import _isEqual from 'lodash/isEqual';
import { useUnmount } from 'react-use';
import { toast } from 'react-toastify';
import classNames from 'classnames';

import { CheckIcon, TrashcanIcon } from 'assets/icons';
import { AlertsList, TasksList, ToastNotificationContent } from 'components/UI';
import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';
import { DatePickerField, Input, Select } from 'components/Forms';
import { formatDropdownOptions } from 'utils/Object';
import { clear, setManualPost, setManualTime } from 'store/clinicalLogger/reducer';
import {
  getCurrentGroup,
  getGroupByGid,
  getTimeLogTasks,
  getTimeLogTypes
} from 'store/groups/reducer';
import { toHHMMSS, toSeconds } from 'utils/Time';
import { removeDuplicatedAlerts } from 'utils/Helpers/Alerts';
import { isPatientCCMOnly } from 'utils/carePrograms';
import { filterTypesOptions } from 'utils/timeLogging';
import { TaskStatus, TimelogTypes } from 'constants/enums';
import { isEHRNotesEnabledForPatient } from 'utils/EHRs';
import { getPatient } from 'store/patient/service';
import { TIME_LOG_UNDEFINED_TYPE } from 'constants/constants';
import { isPatientArchived } from 'store/patient/reducer';
import { useAppSelector, useCanDeleteClinicalLog } from 'hooks';
import { getUserTimeLogType } from 'store/settings/reducer';

import { DeleteConfirmationFooter } from './DeleteConfirmationFooter';

interface IClinicalLoggerModalProps {
  hideTimer?: boolean;
  onPost: (param?: boolean) => void;
  time?: number;
  selectedClinicalLog?: IClinicalLog;
  close: any;
  isPaused?: boolean;
}

interface IClinicalLoggerModalForm {
  timeLogTypeId: string | null;
  timeLogTaskId: string | null;
  time_spent: number;
  log_date?: any;
  notes: string;
  sendToEHR: boolean;
}

const ClinicalLoggerModal = ({
  hideTimer,
  onPost,
  time,
  selectedClinicalLog,
  close,
  isPaused
}: IClinicalLoggerModalProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const canDeleteClinicalLog = useCanDeleteClinicalLog();
  const _isPatientArchived = useAppSelector(isPatientArchived);
  const patient = useAppSelector(({ patient }) => patient);
  const isCCMOnlyPatient = isPatientCCMOnly(patient.data?.patientCareProgram || []);
  const group = useAppSelector((state) => {
    return hideTimer
      ? getCurrentGroup(state)
      : getGroupByGid(state, patient.data?.subgroup_gid || '');
  });
  const defaultClinicalTimeLogTypeId = useAppSelector((state) => getUserTimeLogType(state));
  const allAlerts = useAppSelector((state) => Object.values(state.clinicalLogger.alerts).flat());
  const alerts = removeDuplicatedAlerts(allAlerts);
  const tasks = useAppSelector((state) => state.clinicalLogger.tasks);
  const manualTime = useAppSelector((state) => state.clinicalLogger.manualTime);
  const timeLogTasks = useAppSelector((state) => getTimeLogTasks(state));
  const tasksOptions = timeLogTasks
    ? formatDropdownOptions(timeLogTasks, 'timeLogTaskId', 'labelKey')
        .map((task) => {
          return {
            ...task,
            label: intl.formatMessage({
              id: task.label
            })
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];
  const timeLoggingDisabled = group?.timeLogsDisabled;
  const showTimeLogging = selectedClinicalLog
    ? selectedClinicalLog?.data.timeLogTypeId !== TIME_LOG_UNDEFINED_TYPE
    : !timeLoggingDisabled;
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const isEHRNotesEnabled = isEHRNotesEnabledForPatient(patient, group);

  const timeLogTypes = useAppSelector((state) => getTimeLogTypes(state));
  const filteredTimeLogTypes = filterTypesOptions(timeLogTypes || [], isCCMOnlyPatient);

  const typesOptions = filteredTimeLogTypes
    ? formatDropdownOptions(filteredTimeLogTypes, 'timeLogTypeId', 'labelKey')
        .map((task) => {
          return {
            ...task,
            label: intl.formatMessage({
              id: task.label
            })
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];

  const [{ loading: loadingPost, data: postData, error: postError }, post] = useAxios(
    { method: 'POST' },
    { manual: true }
  );
  const [{ loading: loadingTasksPatch, data: patchTasksData, error: patchTasksError }, patchTasks] =
    useAxios({ url: '/clinician-gateway/Tasks/status', method: 'PATCH' }, { manual: true });
  const [{ loading: loadingDelete, error: deleteError }, deleteLog] = useAxios(
    { method: 'DELETE' },
    { manual: true }
  );
  const loading = loadingPost || loadingTasksPatch || loadingDelete;
  const error = postError || patchTasksError || deleteError;

  const getClinicalTimeLogType = (values: IClinicalLoggerModalForm) => {
    return !showTimeLogging ? TIME_LOG_UNDEFINED_TYPE : values.timeLogTypeId;
  };

  const handleSubmit = (values: IClinicalLoggerModalForm) => {
    values = { ...values, notes: encode(values.notes) };
    const alertsData = alerts.map((alert) => alert.alert_id);
    const tasksData = tasks.map((task) => {
      return { id: task.id, uid: task.patientInfo.uid, gid: task.patientInfo.subgroup_gid };
    });
    const EHRNote =
      values.sendToEHR && isEHRNotesEnabled && !selectedClinicalLog && group?.ehrSystem
        ? group.ehrSystem
        : '';
    if (!hideTimer) {
      const log_date = values.log_date
        ? DateTime.fromJSDate(values.log_date).toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
        : selectedClinicalLog?.data.log_date;

      if (selectedClinicalLog) {
        const data: any = {
          ...values,
          uid: patient.data?.uid,
          gid: patient.data?.subgroup_gid,
          close_alerts: [],
          tasks: [],
          time_spent: !showTimeLogging
            ? 0
            : values.time_spent >= 0
              ? values.time_spent
              : selectedClinicalLog?.data.time_spent,
          log_date,
          // TODO: when refactoring clinical logs using the ms stack, use camelCase
          time_log_type_id: getClinicalTimeLogType(values),
          time_log_task_id: values.timeLogTaskId,
          sendToEHR: EHRNote
        };
        post({ url: `/clinician/ClinicalTimeLogs/${selectedClinicalLog?.id}`, data }).then(() => {
          dispatch(setManualPost(true));
          handleClose();
          patient.data?.uid && dispatch(getPatient({ uid: patient.data?.uid }));
          onPost(true);
        });
      } else {
        if (manualTime) {
          const data = {
            timeLog: {
              uid: patient.data?.uid,
              gid: patient.data?.subgroup_gid,
              timeLogTypeId: getClinicalTimeLogType(values),
              timeLogTaskId: values.timeLogTaskId,
              timeSpent: values.time_spent,
              logDate: log_date,
              sendToEhr: EHRNote,
              notes: values.notes
            },
            closedAlerts: [],
            completedTasks: []
          };
          post({ url: '/clinician-gateway/time-logs', data }).then(() => {
            handleClose();
            dispatch(setManualPost(true));
            patient.data?.uid && dispatch(getPatient({ uid: patient.data?.uid }));
            onPost(true);
          });
        } else {
          delete values.log_date;
          const data = {
            timeLog: {
              uid: patient.data?.uid,
              gid: patient.data?.subgroup_gid,
              timeLogTypeId: getClinicalTimeLogType(values),
              timeLogTaskId: values.timeLogTaskId,
              timeSpent: timeLoggingDisabled ? 0 : !isPaused && time ? time : values.time_spent,
              logDate: values.log_date,
              sendToEhr: EHRNote,
              notes: values.notes
            },
            closedAlerts: alerts.map((alert) => alert.alert_id),
            completedTasks: tasks
          };
          post({ url: '/clinician-gateway/time-logs', data }).then(() => {
            dispatch(setManualPost(true));
            onPost();
            dispatch(clear());
          });
        }
      }
    } else {
      const promises = [];
      if (alertsData.length)
        promises.push(post({ url: '/clinician/Alert/Closed', data: { alerts: alertsData } }));
      if (tasksData.length)
        promises.push(
          patchTasks({
            data: { status: parseInt(TaskStatus.COMPLETED), tasks: tasksData }
          })
        );

      Promise.all(promises).then(() => {
        dispatch(setManualPost(true));
        onPost();
        dispatch(clear());
        close();
      });
    }
  };

  const handleDelete = () => {
    deleteLog({ url: `/clinician-gateway/time-logs/${selectedClinicalLog?.id}` })
      .then(() => {
        dispatch(setManualPost(true));
        handleClose();
        patient.data?.uid && dispatch(getPatient({ uid: patient.data?.uid }));
        onPost(true);
        toast(
          <ToastNotificationContent
            icon={<CheckIcon className="icon" />}
            text="actionLogDeletedKey"
          />
        );
      })
      .catch(() => null);
  };

  const validation = (values: IClinicalLoggerModalForm) => {
    const errors: any = {};
    if (!hideTimer) {
      if (!values.timeLogTypeId)
        errors.timeLogTypeId = intl.formatMessage({ id: 'FieldNotEmptyKey' });
      if (!values.timeLogTaskId)
        errors.timeLogTaskId = intl.formatMessage({ id: 'FieldNotEmptyKey' });
      if (!values.time_spent && manualTime)
        errors.time_spent = intl.formatMessage({ id: 'FieldNotEmptyKey' });
    }
    return errors;
  };

  const getCount = () => {
    let str = intl.formatMessage({ id: 'youAreAboutToCloseKey' });
    if (alerts.length) {
      str += ` ${intl.formatMessage({ id: 'alertCountKey' }, { count: alerts.length })}`;
      if (tasks.length) {
        str += ' &';
      }
    }
    if (tasks.length) {
      str += ` ${intl.formatMessage({ id: 'taskCountKey' }, { count: tasks.length })}`;
    }
    return `${str} :`;
  };

  const initialValues: IClinicalLoggerModalForm = {
    // if selected log: use type from the selected log
    // if patient is CCM only and the default log type is one of the RPM types: use CCM
    // else use default log type
    timeLogTypeId: selectedClinicalLog
      ? typesOptions?.[
          typesOptions.findIndex(
            (type) =>
              // TODO: when refactoring clinical logs, use camelCase and remove this interface
              type.value === (selectedClinicalLog?.data as IClinicalTimeLog7k).time_log_type_id
          )
        ]?.value
      : typesOptions?.[
          typesOptions.findIndex((type) => type.value === defaultClinicalTimeLogTypeId)
        ]?.value ||
        (isCCMOnlyPatient &&
          typesOptions?.[typesOptions.findIndex((type) => type.value === TimelogTypes.CCM)]
            ?.value) ||
        null,
    timeLogTaskId: selectedClinicalLog
      ? tasksOptions?.[
          tasksOptions.findIndex(
            (task) =>
              task.value === (selectedClinicalLog?.data as IClinicalTimeLog7k).time_log_task_id
          )
        ]?.value
      : null,
    notes: selectedClinicalLog ? selectedClinicalLog.data.notes : '',
    time_spent: manualTime
      ? 0
      : selectedClinicalLog
        ? parseInt(selectedClinicalLog.data.time_spent)
        : time || 0,
    log_date: (selectedClinicalLog
      ? DateTime.fromMillis(selectedClinicalLog.epoch)
      : DateTime.now()
    ).toJSDate(),
    // sendToEHR: isEHRNotesEnabled,
    sendToEHR: false
  };

  const handleResetStates = useCallback(() => {
    dispatch(setManualTime({ manualTime: false }));
  }, [dispatch]);

  const handleClose = useCallback(() => {
    handleResetStates();
    close();
  }, [handleResetStates, close]);

  useEffect(() => {
    (postData?.length || patchTasksData?.length) && handleClose();
  }, [postData, patchTasksData, handleClose]);

  useUnmount(() => {
    handleClose();
  });

  const handleGetDefaultTime = () => {
    let seconds;
    if (manualTime) {
      seconds = 0;
    } else if (selectedClinicalLog) {
      seconds = selectedClinicalLog.data.time_spent;
    } else {
      seconds = time || 0;
    }
    const formattedTime = toHHMMSS(seconds);
    return formattedTime;
  };

  const Footer = () => {
    const formikContext = useFormikContext<IClinicalLoggerModalForm>();

    if (selectedClinicalLog && confirmingDelete) {
      const data = selectedClinicalLog.data as IClinicalTimeLog7k;
      return (
        <DeleteConfirmationFooter
          title={intl.formatMessage({ id: 'deleteActionLogTitleKey' })}
          message={intl.formatMessage(
            { id: 'deleteActionLogMessageKey' },
            {
              time: toHHMMSS(data.time_spent),
              billingCode: intl.formatMessage({ id: data.time_log_type_label }),
              date: selectedClinicalLog.date
            }
          )}
          loading={loadingDelete}
          onKeep={() => setConfirmingDelete(false)}
          onConfirm={handleDelete}
        />
      );
    }

    return (
      <Modal.Footer>
        {selectedClinicalLog &&
          canDeleteClinicalLog(selectedClinicalLog.gid) &&
          !_isPatientArchived && (
            <Button
              variant="link"
              className="text-danger me-auto"
              onClick={() => setConfirmingDelete(true)}
              data-test="clinical-log-delete-btn"
            >
              <TrashcanIcon className="icon me-2" />
              {intl.formatMessage({ id: 'deleteKey' })}
            </Button>
          )}
        <Button variant="link" onClick={handleClose} data-test="cancel-btn">
          {intl.formatMessage({ id: 'Cancel' })}
        </Button>
        <Button
          data-test="submit-btn"
          variant="primary"
          type="submit"
          disabled={
            (manualTime && formikContext.values.time_spent <= 0) ||
            (selectedClinicalLog && _isEqual(formikContext.values, initialValues))
          }
        >
          {selectedClinicalLog || manualTime
            ? intl.formatMessage({ id: 'saveButtonTitle' })
            : intl.formatMessage({ id: 'closeKey' })}
        </Button>
      </Modal.Footer>
    );
  };

  return (
    <Modal show onHide={handleClose} backdrop="static" size="xl" data-test="clinical-logger-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {selectedClinicalLog
            ? intl.formatMessage({ id: 'actionLoggingKey' })
            : intl.formatMessage({ id: 'documentYourSessionKey' })}
        </Modal.Title>
      </Modal.Header>

      <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={validation}>
        {({ setFieldValue, values }) => {
          return (
            <FormFormik autoComplete="off">
              <Container className="mt-4 mb-4">
                <Row>
                  {error && (
                    <Col>
                      <Error />
                    </Col>
                  )}
                  {loading && (
                    <div data-test="close-document-loading" className="mt-4 mb-4">
                      <Loading />
                    </div>
                  )}
                  {hideTimer && (
                    <Col>
                      <Alert variant="primary" className="mb-4" data-test="bulk-close-notice">
                        {intl.formatMessage(
                          { id: 'bulkCloseNoticeKey' },
                          {
                            type:
                              tasks.length > 0
                                ? intl.formatMessage({ id: 'taskCountKey' }, { count: '' })
                                : intl.formatMessage({ id: 'alertCountKey' }, { count: '' })
                          }
                        )}
                      </Alert>
                    </Col>
                  )}
                </Row>
                <Row>
                  {!selectedClinicalLog && !manualTime && !!(alerts.length || tasks.length) && (
                    <Col lg={12} xl={hideTimer ? 12 : 6} className="mb-3">
                      <div data-test="close-alerts-number">{getCount()}</div>
                      {alerts.length > 0 && (
                        <div className="mb-3">
                          <AlertsList alerts={alerts} />
                        </div>
                      )}
                      {tasks.length > 0 && <TasksList tasks={tasks} />}
                    </Col>
                  )}
                  {!hideTimer && (
                    <Col
                      className={classNames({
                        'mt-4':
                          !selectedClinicalLog && !manualTime && !!(alerts.length || tasks.length)
                      })}
                    >
                      <Form.Group className="form-group">
                        <Select
                          name="timeLogTaskId"
                          dataTestId="clinical-log-modal-action-taken"
                          label={intl.formatMessage({ id: 'actionTakenKey' })}
                          options={tasksOptions}
                          required
                          isDisabled={!!selectedClinicalLog}
                          isSearchable={false}
                        />
                      </Form.Group>
                      {showTimeLogging && (
                        <>
                          <div className="d-flex">
                            <Form.Group className="form-group">
                              <Form.Label>
                                {intl.formatMessage({ id: 'timeSpentKey' })} *
                              </Form.Label>
                              <TimeField
                                value={handleGetDefaultTime()}
                                onChange={(event, value) => {
                                  setFieldValue('time_spent', toSeconds(value));
                                }}
                                input={
                                  <Input
                                    className="font-weight-bolder max-w-100 text-center"
                                    name="time_spent"
                                    required
                                  />
                                }
                                showSeconds
                                data-test="clinical-log-modal-time-spent"
                              />
                            </Form.Group>

                            <Form.Group className="form-group flex-grow-1 ms-3">
                              <Select
                                name="timeLogTypeId"
                                label={intl.formatMessage({ id: 'billingCodeKey' })}
                                options={typesOptions}
                                required
                                dataTestId="clinical-log-modal-billing-code"
                              />
                            </Form.Group>
                          </div>

                          {
                            // If editing or manually adding a clinical log, show Date field
                            (manualTime || selectedClinicalLog) && (
                              <Form.Group className="form-group">
                                <DatePickerField
                                  name="log_date"
                                  label={intl.formatMessage({ id: 'dateKey' })}
                                  className="w-100"
                                  wrapperClassName="w-100"
                                  maxDate={DateTime.now().toJSDate()}
                                  maxTime={DateTime.now().toJSDate()}
                                  showTimeInput
                                  required
                                  disabled={!!selectedClinicalLog}
                                  dataTestId="clinical-log-modal-date"
                                />
                              </Form.Group>
                            )
                          }
                        </>
                      )}

                      <Form.Group className="form-group">
                        <div className="d-flex justify-content-between">
                          <Form.Label>{intl.formatMessage({ id: 'notesKey' })}</Form.Label>
                          {isEHRNotesEnabled && !selectedClinicalLog && (
                            <Form.Check
                              id="send_ehr_switch"
                              type="switch"
                              label={intl.formatMessage({ id: 'noteToEHRKey' })}
                              checked={values.sendToEHR}
                              onChange={() => setFieldValue('sendToEHR', !values.sendToEHR)}
                            />
                          )}
                        </div>
                        <Input
                          name="notes"
                          as="textarea"
                          disabled={!!selectedClinicalLog}
                          data-test="clinical-log-modal-notes"
                          id="clinicalLog-notes"
                        />
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              </Container>
              <Footer />
            </FormFormik>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ClinicalLoggerModal;
